import { createSlice } from "@reduxjs/toolkit";
import { getCutoffsAdvisorOverviewsPublicIDPdf } from "Api/Api";
import { mapAPIErrorResponse } from "Models/Errors/ApiCallError";
import { Resources, getTranslation } from "Translations/Resources";
import { downloadBase64File } from "Utils/FileUtils";
import { call, put, takeLatest } from "typed-redux-saga";
import { createAsyncAction, getType } from "typesafe-actions";

export const getCutoffAdvisorOverviewPdfAsync = createAsyncAction(
  "@cutoff/GET_ADVISOR_OVERVIEW_PDF_REQUEST",
  "@cutoff/GET_ADVISOR_OVERVIEW_PDF_RESPONSE",
  "@cutoff/GET_ADVISOR_OVERVIEW_PDF_FAILURE",
)<{ cutoffPublicID: string; period: string }, void, Error>();

function* getCutoffAdvisorOverviewPdf(
  action: ReturnType<typeof getCutoffAdvisorOverviewPdfAsync.request>,
): Generator {
  try {
    const { data, error, status } = yield* call(
      getCutoffsAdvisorOverviewsPublicIDPdf,
      action.payload.cutoffPublicID,
    );

    if (status === 200) {
      downloadBase64File(
        data.cutoffAdvisorOverviewPdf,
        "application/pdf",
        `${getTranslation(Resources.Settings.Cutoff.AdvisorOverview.Title)}-${
          action.payload.period
        }.pdf`,
      );
      yield put(getCutoffAdvisorOverviewPdfAsync.success());
      return;
    }

    yield put(
      getCutoffAdvisorOverviewPdfAsync.failure(mapAPIErrorResponse(error ?? data)),
    );
  } catch (err) {
    yield put(getCutoffAdvisorOverviewPdfAsync.failure(err as Error));
  }
}

export function* getCutoffAdvisorOverviewPdfSaga() {
  yield takeLatest(
    getType(getCutoffAdvisorOverviewPdfAsync.request),
    getCutoffAdvisorOverviewPdf,
  );
}

type GetCutoffAdvisorOverviewPdfSliceState = {
  cutoffPublicID: string | null;
  isLoading: boolean;
  error: Error | null;
};

const initialState: GetCutoffAdvisorOverviewPdfSliceState = {
  cutoffPublicID: null,
  isLoading: false,
  error: null,
};

export const getCutoffAdvisorOverviewPdfSlice = createSlice({
  initialState,
  name: "@cutoff/getCutoffAdvisorOverviewPdf",
  reducers: {},
  extraReducers: builder => {
    builder.addCase(
      getType(getCutoffAdvisorOverviewPdfAsync.request),
      (state, action: ReturnType<typeof getCutoffAdvisorOverviewPdfAsync.request>) => {
        state.cutoffPublicID = action.payload.cutoffPublicID;
        state.error = null;
        state.isLoading = true;
      },
    );
    builder.addCase(
      getType(getCutoffAdvisorOverviewPdfAsync.success),
      (state, action: ReturnType<typeof getCutoffAdvisorOverviewPdfAsync.success>) => {
        state.cutoffPublicID = null;
        state.isLoading = false;
      },
    );
    builder.addCase(
      getType(getCutoffAdvisorOverviewPdfAsync.failure),
      (state, action: ReturnType<typeof getCutoffAdvisorOverviewPdfAsync.failure>) => {
        state.cutoffPublicID = null;
        state.isLoading = false;
        state.error = action.payload;
      },
    );
  },
});
